/**
 * SidebarV2 — the v2 left nav rail (UI-v2 Phase 1 shell fidelity).
 *
 * Canonical source: design/v2-direction/index.html lines 632–683 + CSS 186–261.
 *
 * Structure: brand header (56px) → nav (.nav: 12px pad, 2px gap, flex-col) with
 * three .nav-label groups (WORKSPACE / INSIGHTS / ACCOUNT) → .sidebar-foot
 * (margin-top:auto, 12px pad, border-top) with the .user-chip trigger.
 *
 * Fidelity vs Phase 1a placeholder:
 *   - 244px rail (w-sidebar-w-v2), 56px brand header (h-[56px]).
 *   - Workspace-switch button (.ws) — presentational, ruling A.
 *   - Three grouped sections; ACCOUNT group wraps Settings as a crisp row.
 *   - .nav-label: 11px / 600 / uppercase / 0.05em tracking / text-tertiary.
 *   - .nav-item: 13.5px/500, pad 7px 12px, gap 10px, 17×17 icons at opacity .9.
 *   - Active blue rail: 3px, left:-12px, 18px tall, rounded-right, accent-primary.
 *     Active icon also turns text-accent-primary (canonical line 241).
 *   - to:null rows: CRISP full text-text-secondary, hoverable, zero opacity-50.
 *     aria-disabled for a11y but NO muted styling.
 *   - Count pills: wired to live API counts (ruling B — no fake numbers).
 *   - .user-chip (AccountMenuV2) in .sidebar-foot, menu opens upward.
 *
 * Palette: blue = --accent-primary (active rail + icon). Teal = wordmark dot only.
 */
import { Link, NavLink } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { Wordmark } from "@/components/common/Wordmark";
import {
  DashboardIcon,
  CandidatesIcon,
  JobsIcon,
  IntelligenceIcon,
  OutcomesIcon,
  JournalIcon,
  SettingsIcon,
  HelpIcon,
  type IconProps,
} from "@/components/layout/SidebarIcons";
import { useAuthStore } from "@/stores/useAuthStore";
import { AccountMenuV2 } from "@/components/v2/layout/AccountMenuV2";
import { useCandidates } from "@/hooks/queries/candidates/useCandidates";
import { useJds } from "@/hooks/queries/candidates/useJds";
import { cn } from "@/lib/utils";

interface NavRow {
  label: string;
  icon: (props: IconProps) => JSX.Element;
  /** Sub-path under /${tenantSlug}/v2. null = fan-out batch will wire this. */
  to: string | null;
  /** Live count pill. Omitted when undefined (no fake numbers). */
  count?: number;
  /**
   * Whether the NavLink should use `end` (exact match only).
   * Default true. Set false for section roots (jobs, candidates) so the
   * nav item stays active on sub-routes (jobs/:jdId, jobs/new, etc.).
   */
  end?: boolean;
  /**
   * 1d alternative spelling: when true the NavLink uses prefix matching
   * (end=false) so sub-paths also highlight this row (e.g. Settings stays
   * active on /settings/security). Equivalent to `end: false`; kept so the
   * settings row reads declaratively. If set, it overrides `end`.
   */
  prefixMatch?: boolean;
}

/** Chevrons-up/down icon for the workspace-switch button. 13×13 per canon. */
function ChevronsUpDownIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      width={13}
      height={13}
      aria-hidden
    >
      <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
    </svg>
  );
}

/**
 * ChevronRightSmIcon — 14×14 separator for the topbar breadcrumb.
 * Exported so TopbarV2 can import without a separate file.
 */
export function ChevronRightSmIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      width={14}
      height={14}
      className={className}
      aria-hidden
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function SidebarV2({ v2Dark = false }: { v2Dark?: boolean }): JSX.Element {
  const base = useTenantBase();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const role = useAuthStore((s) => s.role);
  const isAdmin = isSuperAdmin || role === "admin";

  // Live counts for count pills (ruling B — wire if trivially available; no fake numbers).
  const candidatesQuery = useCandidates();
  const jdsQuery = useJds();
  const candidateCount: number | undefined = candidatesQuery.data?.length;
  const jdCount: number | undefined = jdsQuery.data?.length;

  const WORKSPACE: NavRow[] = [
    { label: "Dashboard", icon: DashboardIcon, to: "dashboard" },
    // Phase 1b-rem wired: "All Candidates" → candidates; end:false keeps active
    // on sub-routes (candidates/new, candidates/:candidateId).
    { label: "All Candidates", icon: CandidatesIcon, to: "candidates", count: candidateCount, end: false },
    // Phase 1c wired: "Manage Jobs" → jobs sub-path; end:false keeps active on sub-routes.
    { label: "Manage Jobs", icon: JobsIcon, to: "jobs", count: jdCount, end: false },
  ];

  const INSIGHTS: NavRow[] = [
    // Phase 1b-rem wired: "Hiring Intelligence" → intelligence sub-path.
    { label: "Hiring Intelligence", icon: IntelligenceIcon, to: "intelligence" },
    // PP-4 wired: "Outcomes" → outcomes overview (post-placement tracking).
    // end:false keeps it active on the placed-candidate sub-route (outcomes/:id).
    { label: "Outcomes", icon: OutcomesIcon, to: "outcomes", end: false },
    // Phase 1c wired: "Actions Log" → journal sub-path.
    { label: "Actions Log", icon: JournalIcon, to: "journal", end: true },
  ];

  const ACCOUNT_ROWS: NavRow[] = [
    { label: "Settings", icon: SettingsIcon, to: "settings", prefixMatch: true },
  ];

  // Help Center — reachable from anywhere, available to ALL signed-in tenant
  // users (NOT admin-gated, unlike ACCOUNT). Rendered as the last nav group so
  // it sits just above the account footer, near the bottom of the rail.
  // `prefixMatch` keeps it active on /help/:slug doc sub-routes.
  const SUPPORT_ROWS: NavRow[] = [
    { label: "Help Center", icon: HelpIcon, to: "help", prefixMatch: true },
  ];

  return (
    /*
     * Canonical .sidebar: width:244px; flex:0 0 244px; position:sticky; top:0;
     * height:100vh; border-right; flex-direction:column.
     * w-sidebar-w-v2 = 244px (v2-scoped token, does NOT touch the default sidebar-w).
     */
    <aside
      className="flex shrink-0 flex-col border-r border-border-default bg-surface-app-chrome w-sidebar-w-v2"
      style={{ position: "sticky", top: 0, height: "100vh" }}
    >
      {/* ── Brand header (.brand): h:56px, px-4, border-bottom, flex align-center gap-10px ── */}
      <div
        className="flex shrink-0 items-center border-b border-border-default"
        style={{ height: "56px", padding: "0 16px", gap: "10px" }}
      >
        <Link
          to={`${base}/dashboard`}
          aria-label="CertAIn — go to Dashboard"
          className="flex cursor-pointer items-center rounded-sm transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          <Wordmark height={26} surface={v2Dark ? "dark" : "light"} />
        </Link>

        {/*
         * Workspace-switch button (.ws): margin-left:auto; padding:5px;
         * border:1px solid border-default; border-radius; 13×13 icon; text-tertiary.
         * Presentational (no workspace switcher exists yet) — ruling A.
         */}
        <button
          type="button"
          aria-label="Switch workspace"
          title="Switch workspace"
          className="ml-auto inline-flex cursor-pointer items-center rounded-[var(--radius,0.5rem)] border border-border-default p-[5px] text-text-tertiary transition-colors hover:bg-surface-card-hover"
        >
          <ChevronsUpDownIcon />
        </button>
      </div>

      {/* ── Nav (.nav): padding:12px, flex-col, gap:2px, overflow-y:auto ── */}
      <nav
        aria-label="Primary"
        className="flex flex-col overflow-y-auto"
        style={{ padding: "12px", gap: "2px" }}
      >
        <NavGroup label="Workspace" rows={WORKSPACE} base={base} />
        <NavGroup label="Insights" rows={INSIGHTS} base={base} />
        {isAdmin && (
          <NavGroup label="Account" rows={ACCOUNT_ROWS} base={base} />
        )}
        {/* Support group — always shown (all tenant users can reach Help). */}
        <NavGroup label="Support" rows={SUPPORT_ROWS} base={base} />
      </nav>

      {/* ── Sidebar footer (.sidebar-foot): margin-top:auto, padding:12px, border-top ── */}
      <div
        className="mt-auto border-t border-border-default"
        style={{ padding: "12px" }}
      >
        {/* AccountMenuV2 renders the .user-chip + upward-opening menu */}
        <AccountMenuV2 />
      </div>
    </aside>
  );
}

/**
 * NavGroup — one labelled nav section (.nav-label + rows).
 * Canonical .nav-label: 11px / weight 600 / uppercase / letter-spacing 0.05em /
 * color muted / padding 14px 12px 6px.
 */
function NavGroup({
  label,
  rows,
  base,
}: {
  label: string;
  rows: NavRow[];
  base: string;
}): JSX.Element {
  return (
    <div>
      <p
        className="text-text-tertiary"
        style={{
          fontSize: "11px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "14px 12px 6px",
        }}
      >
        {label}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {rows.map((row) => (
          <NavRowItem key={row.label} row={row} base={base} />
        ))}
      </div>
    </div>
  );
}

/**
 * NavRowItem — canonical .nav-item.
 *
 * Active state (Dashboard only at shell stage):
 *   - bg-surface-card-hover, font-weight:600
 *   - ::before blue rail: 3px wide, left:-12px, 18px tall, rounded-right,
 *     bg-accent-primary (Tailwind: before:left-[-12px] before:top-1/2
 *     before:-translate-y-1/2 before:w-[3px] before:h-[18px] before:rounded-r
 *     before:bg-accent-primary before:content-[''])
 *   - active svg: text-accent-primary (blue)
 *
 * to=null rows: CRISP — full text-text-secondary, hoverable.
 *   aria-disabled for a11y but ZERO muted styling (spec §3, non-negotiable 2).
 *   Rendered as a span (not NavLink) so clicking is inert.
 */
function NavRowItem({ row, base }: { row: NavRow; base: string }): JSX.Element {
  const { icon: Icon, label, to, count, end: endProp = true, prefixMatch } = row;
  // `prefixMatch` (1d) is the inverse spelling of `end` (1b/1c). When set it
  // wins (prefixMatch → end=false); otherwise fall back to `end` (default true).
  const linkEnd = prefixMatch !== undefined ? !prefixMatch : endProp;

  // Shared style constants (canonical measurements — padding/gap only; no raw color).
  const itemStyle = { padding: "7px 12px", gap: "10px" } as const;
  // Text metrics via inline style since 13.5px is not a named Tailwind step.
  const textStyle = { fontSize: "13.5px", fontWeight: 500 } as const;
  // Icon sizing/opacity via className (IconProps doesn't accept style prop).
  // w-[17px] h-[17px] matches canonical .nav-item svg { width:17px; height:17px }.
  const iconInactiveClass = "w-[17px] h-[17px] shrink-0 opacity-[.9] text-current";
  const iconActiveClass   = "w-[17px] h-[17px] shrink-0 opacity-100 text-[var(--nav-active-icon)]";

  if (to === null) {
    /*
     * Crisp-but-inert row. aria-disabled set for a11y.
     * NO text-text-disabled, NO opacity-50 — full crisp styling (spec §3).
     */
    return (
      <span
        aria-disabled
        role="button"
        tabIndex={-1}
        className={cn(
          "relative flex cursor-default items-center rounded-[calc(var(--radius,0.5rem)-2px)]",
          "text-text-secondary transition-colors",
          "hover:bg-[var(--nav-hover-bg)] hover:text-text-primary",
        )}
        style={itemStyle}
      >
        <Icon className={iconInactiveClass} aria-hidden />
        <span className="truncate text-current" style={textStyle}>
          {label}
        </span>
        {count !== undefined ? <CountPill count={count} /> : null}
      </span>
    );
  }

  /* Routed row — real NavLink (Dashboard today). */
  return (
    <NavLink
      to={`${base}/${to}`}
      end={linkEnd}
      className={({ isActive }) =>
        cn(
          "relative flex cursor-pointer items-center rounded-[calc(var(--radius,0.5rem)-2px)]",
          "transition-colors",
          isActive
            ? // Active state: blue-tinted bg, bold text, blue left rail via before pseudo.
              "bg-[var(--nav-active-bg)] text-text-primary font-semibold" +
              " before:absolute before:left-[-12px] before:top-1/2 before:-translate-y-1/2" +
              " before:w-[3px] before:h-[18px] before:rounded-r before:bg-accent-primary before:content-['']"
            : "text-text-secondary hover:bg-[var(--nav-hover-bg)] hover:text-text-primary",
        )
      }
      style={itemStyle}
    >
      {({ isActive }) => (
        <>
          <Icon
            className={isActive ? iconActiveClass : iconInactiveClass}
            aria-hidden
          />
          <span className="truncate text-current" style={textStyle}>
            {label}
          </span>
          {count !== undefined ? <CountPill count={count} /> : null}
        </>
      )}
    </NavLink>
  );
}

/**
 * CountPill — canonical .count: margin-left:auto, 11.5px tabular, pill bg/text-secondary.
 */
function CountPill({ count }: { count: number }): JSX.Element {
  return (
    <span
      className="ml-auto shrink-0 rounded-full bg-surface-card-hover text-text-secondary"
      style={{
        fontSize: "11.5px",
        fontWeight: 500,
        fontVariantNumeric: "tabular-nums",
        padding: "1px 8px",
      }}
    >
      {count}
    </span>
  );
}
